import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  Filter,
  ListFilter,
  Loader2,
  MoreHorizontal,
  Plus,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { tasksApi } from "@/services/api/tasks";
import { cn } from "@/lib/utils";
import { TASK_ENTRY_PATH } from "@/stores";
import type { TaskRun, TaskRunStatus } from "@/types/task";

export const Route = createFileRoute("/_authenticated/tasks/")({
  component: TasksPage,
});

type TaskColumnKey = "pending" | "running" | "completed" | "archived";
type TaskBoardItem = { task: TaskRun; code: string };
type TaskBoardColumnState = { items: TaskBoardItem[]; totalCount: number };

interface TaskColumnConfig {
  key: TaskColumnKey;
  title: string;
  statuses: TaskRunStatus[];
  dotClass: string;
}

const TASK_COLUMNS: TaskColumnConfig[] = [
  {
    key: "pending",
    title: "待执行",
    statuses: ["draft", "upcoming"],
    dotClass: "border-muted-foreground",
  },
  {
    key: "running",
    title: "执行中",
    statuses: ["in_progress", "paused", "pending_action"],
    dotClass: "border-blue-500 text-blue-500",
  },
  {
    key: "completed",
    title: "已结束",
    statuses: ["completed", "failed", "stopped", "timeout"],
    dotClass: "border-sky-600 text-sky-600",
  },
  {
    key: "archived",
    title: "归档",
    statuses: [],
    dotClass: "border-muted-foreground text-muted-foreground",
  },
];

const TASK_COLUMN_BY_STATUS = new Map<TaskRunStatus, TaskColumnKey>(
  TASK_COLUMNS.flatMap((column) =>
    column.statuses.map((status) => [status, column.key] as const),
  ),
);

const TASK_BOARD_MAX_VISIBLE_PER_COLUMN = 50;
const INITIAL_VISIBLE_COUNTS: Record<TaskColumnKey, number> = {
  pending: TASK_BOARD_MAX_VISIBLE_PER_COLUMN,
  running: TASK_BOARD_MAX_VISIBLE_PER_COLUMN,
  completed: TASK_BOARD_MAX_VISIBLE_PER_COLUMN,
  archived: TASK_BOARD_MAX_VISIBLE_PER_COLUMN,
};

function TasksPage() {
  const { t } = useTranslation("navigation");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [visibleCounts, setVisibleCounts] = useState(INITIAL_VISIBLE_COUNTS);

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ["tasks"],
    queryFn: () => tasksApi.list(),
  });

  const grouped = useMemo(() => {
    const initial: Record<TaskColumnKey, TaskBoardColumnState> = {
      pending: { items: [], totalCount: 0 },
      running: { items: [], totalCount: 0 },
      completed: { items: [], totalCount: 0 },
      archived: { items: [], totalCount: 0 },
    };

    for (const [index, task] of tasks.entries()) {
      const key = task.archivedAt
        ? "archived"
        : (TASK_COLUMN_BY_STATUS.get(task.status) ?? "pending");
      const column = initial[key];
      column.totalCount += 1;

      column.items.push({
        task,
        code: `T9-${String(index + 1).padStart(2, "0")}`,
      });
    }

    return initial;
  }, [tasks]);

  const openTask = (task: TaskRun) => {
    void (async () => {
      try {
        if (task.hiddenAt) {
          await tasksApi.unhide(task.id);
          await queryClient.invalidateQueries({ queryKey: ["tasks"] });
        }
      } finally {
        void navigate({
          to: "/tasks/$taskId",
          params: { taskId: task.id },
        });
      }
    })();
  };

  const openNewTask = () => {
    void navigate({ to: TASK_ENTRY_PATH });
  };

  const loadMoreTasks = (columnKey: TaskColumnKey) => {
    setVisibleCounts((current) => ({
      ...current,
      [columnKey]: current[columnKey] + TASK_BOARD_MAX_VISIBLE_PER_COLUMN,
    }));
  };

  return (
    <div
      data-testid="tasks-board-page"
      className="flex h-full min-w-0 flex-col bg-background text-foreground"
    >
      <header className="flex h-16 shrink-0 items-center justify-between border-b border-border bg-background px-6">
        <div className="flex items-center gap-3">
          <button className="flex h-10 items-center gap-3 rounded-lg border border-border bg-card px-4 text-sm text-muted-foreground transition-colors hover:bg-accent/40">
            <span>任务用户</span>
            <span className="font-semibold text-foreground">自己</span>
          </button>
          <button className="flex h-10 items-center gap-3 rounded-lg border border-border bg-card px-4 text-sm text-muted-foreground transition-colors hover:bg-accent/40">
            <span>Agent</span>
            <span className="font-semibold text-foreground">全部</span>
          </button>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" aria-label="筛选任务">
            <Filter size={17} />
          </Button>
          <Button variant="outline" size="icon" aria-label="任务排序">
            <ListFilter size={17} />
          </Button>
          <Button
            className="bg-nav-foreground text-nav-sub-bg hover:bg-nav-foreground-strong"
            onClick={openNewTask}
          >
            <Plus size={17} />
            新增任务
          </Button>
        </div>
      </header>

      <main
        data-testid="tasks-board-main"
        className="min-h-0 flex-1 overflow-x-auto bg-background p-6"
      >
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid min-h-full min-w-[980px] grid-cols-4 gap-6">
            {TASK_COLUMNS.map((column) => (
              <TaskColumn
                key={column.key}
                column={column}
                items={grouped[column.key].items}
                totalCount={grouped[column.key].totalCount}
                visibleCount={visibleCounts[column.key]}
                onOpenTask={openTask}
                onLoadMore={() => loadMoreTasks(column.key)}
              />
            ))}
          </div>
        )}
      </main>

      <span className="sr-only">{t("tasks", "Tasks")}</span>
    </div>
  );
}

function TaskColumn({
  column,
  items,
  totalCount,
  visibleCount,
  onOpenTask,
  onLoadMore,
}: {
  column: TaskColumnConfig;
  items: TaskBoardItem[];
  totalCount: number;
  visibleCount: number;
  onOpenTask: (task: TaskRun) => void;
  onLoadMore: () => void;
}) {
  const visibleItems = items.slice(0, visibleCount);
  const hiddenTaskCount = Math.max(0, totalCount - visibleItems.length);

  return (
    <section
      data-testid={`task-column-${column.key}`}
      className={cn(
        "min-h-full rounded-lg border border-border/60 bg-muted/25 px-4 py-4",
        column.key === "running" && "bg-accent/35",
      )}
    >
      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <span
            className={cn(
              "inline-flex size-4 items-center justify-center rounded-full border-2",
              column.dotClass,
            )}
          />
          <span>{column.title}</span>
          <span className="text-muted-foreground">{totalCount}</span>
        </div>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`${column.title}更多`}
        >
          <MoreHorizontal size={16} />
        </Button>
      </div>

      <div className="space-y-3">
        {visibleItems.map(({ task, code }) => (
          <TaskCard
            key={task.id}
            task={task}
            code={code}
            onClick={() => onOpenTask(task)}
          />
        ))}
        {hiddenTaskCount > 0 ? (
          <Button
            type="button"
            variant="outline"
            className="w-full justify-center gap-2 border-dashed bg-background/70 text-sm text-muted-foreground hover:bg-accent/45 hover:text-foreground"
            aria-label={`加载更多${column.title}任务，还有 ${hiddenTaskCount} 个`}
            onClick={onLoadMore}
          >
            <ChevronDown size={16} />
            <span>加载更多</span>
            <span className="text-xs">还有 {hiddenTaskCount} 个</span>
          </Button>
        ) : null}
      </div>
    </section>
  );
}

function TaskCard({
  task,
  code,
  onClick,
}: {
  task: TaskRun;
  code: string;
  onClick: () => void;
}) {
  const agentLabel = task.botId ? "@Agent" : "我的任务";

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-lg border border-border bg-card p-4 text-left shadow-sm transition hover:bg-accent/40 hover:shadow-md"
    >
      <div className="mb-3 flex items-center justify-between text-xs text-muted-foreground">
        <span>{code}</span>
        <MoreHorizontal size={16} />
      </div>
      <h3 className="line-clamp-2 text-[15px] font-semibold leading-6 text-card-foreground">
        {task.title}
      </h3>
      <p className="mt-2 line-clamp-2 text-sm leading-6 text-muted-foreground">
        <span className="font-medium text-blue-600">{agentLabel}</span>{" "}
        {task.description ?? "等待补充任务目标、上下文和交付要求。"}
      </p>
      <p className="mt-4 text-sm text-muted-foreground">自己</p>
    </button>
  );
}
